import {AfterViewInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ThemesService} from "../../../services/themes.service";
import {Subject, takeUntil} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, ParamMap, Router, NavigationEnd} from "@angular/router";
import {filter} from "rxjs/operators";

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.sass']
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Input() title: string | null = null;
  @Input() logoPath: string | null = null;
  @Input() messages: string | null = null;
  @Input() projectClaim: string = '';
  @Input() projectLine1: string = '';
  @Input() projectLine2: string = '';
  public projectDetails = new Subject();
  public loading = true;
  public projectShortname: any;
  private ngUnsubscribe = new Subject();
  private isDataGateRoute: boolean = false;

  constructor(
    private themeService: ThemesService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    // Check if we're on a data-gate route - use setTimeout to ensure router is ready
    setTimeout(() => {
      this.checkIfDataGateRoute();
      this.loadSidebarTranslations();
    }, 0);

    // Subscribe to project details once (will receive updates when API is called)
    // Only subscribe if not on data-gate route
    if (!this.isDataGateRoute) {
      this.setupProjectDetailsSubscription();
    }

    // Get project shortname from route parameters directly
    this.loadProjectShortnameFromRoute();
    
    // Listen to route parameter changes for direct navigation
    this.activatedRoute.params.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.checkIfDataGateRoute();
      this.loadProjectShortnameFromRoute();
      this.loadSidebarTranslations();
    });

    // Also listen to parent route params (in case sidebar is in a child route)
    if (this.activatedRoute.parent) {
      this.activatedRoute.parent.params.pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((params) => {
        this.checkIfDataGateRoute();
        if (params['projectShortname']) {
          const newProjectShortname = params['projectShortname'];
          if (newProjectShortname !== this.projectShortname) {
            this.projectShortname = newProjectShortname;
            this.themeService.setProjectShortName(this.projectShortname);
            if (!this.isDataGateRoute) {
              this.themeService.getProjectDetails();
            }
          }
        }
        this.loadSidebarTranslations();
      });
    }

    // Listen to router navigation events to reload on direct links
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.checkIfDataGateRoute();
      this.loadProjectShortnameFromRoute();
      this.loadSidebarTranslations();
    });

    // Fallback: also listen to themeService (for backward compatibility)
    this.themeService.getProjectShortName().pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((x) => {
      if (x && x !== this.projectShortname) {
        this.projectShortname = x;
        if (!this.isDataGateRoute) {
          this.themeService.getProjectDetails();
        }
      }
    });
  }

  private setupProjectDetailsSubscription(): void {
    // Subscribe once to project details - will receive updates whenever getProjectDetails() is called
    // Only update from API if not on data-gate route
    this.themeService.getDetailProjectDetails().pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((x: any) => {
      // Don't overwrite i18n values if we're on a data-gate route
      if (this.isDataGateRoute) return;
      if (!this.projectShortname) return;

      const projectShortnameUpper = this.projectShortname?.toUpperCase();
      if (!projectShortnameUpper) return;

      let project: any = null;

      // Variante 1: API liefert eine Liste von Projekten
      if (Array.isArray(x)) {
        project = x.find((y: any) => y?.shortname === projectShortnameUpper);
      }
      // Variante 2: API liefert ein einzelnes Projekt-Objekt (z.B. dein OCD-JSON)
      else if (x && typeof x === 'object') {
        if (!x.shortname || x.shortname === projectShortnameUpper) {
          project = x;
        }
      }

      if (project) {
        // Theme-spezifischen Claim und Title aus Query-Parameter ermitteln (z.B. ?theme=OCS)
        let themeSidebarClaim: string | null = null;
        let themeTitleInApp: string | null = null;
        const themeParam = this.activatedRoute.snapshot.queryParamMap.get('theme');

        if (themeParam && project.themes) {
          const themeParamLower = themeParam.toLowerCase();

          // Variante A: themes ist ein Array von Objekten: [ { "OCS": { ... } }, { "GSH": { ... } } ]
          if (Array.isArray(project.themes) && project.themes.length > 0) {
            for (const themeEntry of project.themes) {
              if (!themeEntry || typeof themeEntry !== 'object') {
                continue;
              }

              const matchingKey = Object.keys(themeEntry).find(
                (key) => key.toLowerCase() === themeParamLower
              );

              if (matchingKey) {
                const themeConfig = (themeEntry as any)[matchingKey];
                if (themeConfig && typeof themeConfig === 'object') {
                  if (themeConfig.sidebar_claim) {
                    themeSidebarClaim = themeConfig.sidebar_claim;
                  }
                  if (themeConfig.title_in_app) {
                    themeTitleInApp = themeConfig.title_in_app;
                  }
                }
                break;
              }
            }
          }
          // Variante B: themes ist ein Objekt: { "OCS": { ... }, "GSH": { ... } }
          else if (typeof project.themes === 'object') {
            const themesObj = project.themes as any;
            const matchingKey = Object.keys(themesObj).find(
              (key) => key.toLowerCase() === themeParamLower
            );
            if (matchingKey) {
              const themeConfig = themesObj[matchingKey];
              if (themeConfig && typeof themeConfig === 'object') {
                if (themeConfig.sidebar_claim) {
                  themeSidebarClaim = themeConfig.sidebar_claim;
                }
                if (themeConfig.title_in_app) {
                  themeTitleInApp = themeConfig.title_in_app;
                }
              }
            }
          }
        }

        // Titel überschreiben, wenn title_in_app vorhanden ist, sonst bisherigen Titel beibehalten
        if (themeTitleInApp) {
          this.title = themeTitleInApp;
        }

        // Priorität für Claim:
        // 1) themeSidebarClaim (falls gesetzt)
        // 2) project.project_claim
        // 3) bestehender Wert (z.B. Übersetzung / Fallback)
        this.projectClaim = themeSidebarClaim || project.project_claim || this.projectClaim;
        this.projectLine1 = project.first_line || this.projectLine1;
        this.projectLine2 = project.second_line || this.projectLine2;
      }
    });
  }

  private checkIfDataGateRoute(): void {
    // Check router URL
    const routerUrl = this.router.url || '';
    // Check window location as fallback
    const windowUrl = window.location.pathname || '';
    // Check route snapshot
    const routePath = this.activatedRoute.snapshot.routeConfig?.path || '';
    
    this.isDataGateRoute = 
      routerUrl.includes('/datagate/') || 
      routerUrl.includes('/data-gate/') ||
      windowUrl.includes('/datagate/') || 
      windowUrl.includes('/data-gate/') ||
      routePath.includes('datagate') ||
      routePath.includes('data-gate');
  }

  private loadSidebarTranslations(): void {
    // Wait for translations to be ready
    this.translateService.onLangChange.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.loadTranslations();
    });

    // Wait for default language to be set
    this.translateService.onDefaultLangChange.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.loadTranslations();
    });

    // Also load immediately - use get() which returns Observable and handles async loading
    // But add a small delay to ensure router is ready
    setTimeout(() => {
      this.loadTranslations();
    }, 100);
  }

  private loadTranslations(): void {
    if (this.isDataGateRoute) {
      // Use DataGate i18n keys for data-gate routes
      // Try instant first, fallback to get() if not available
      try {
        const instantTitle = this.translateService.instant('DataGate.DatagateSidebarTitle');
        if (instantTitle && instantTitle !== 'DataGate.DatagateSidebarTitle') {
          this.title = instantTitle;
        }
      } catch (e) {
        // Fall through to get()
      }
      
      this.translateService.get('DataGate.DatagateSidebarTitle').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (x && x !== 'DataGate.DatagateSidebarTitle') {
          this.title = x;
        }
      });

      try {
        const instantClaim = this.translateService.instant('DataGate.DatagateSidebarClaim');
        if (instantClaim && instantClaim !== 'DataGate.DatagateSidebarClaim') {
          this.projectClaim = instantClaim;
        }
      } catch (e) {
        // Fall through to get()
      }
      
      this.translateService.get('DataGate.DatagateSidebarClaim').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (x && x !== 'DataGate.DatagateSidebarClaim') {
          this.projectClaim = x;
        }
      });

      try {
        const instantLine1 = this.translateService.instant('DataGate.DatagateSidebarProjectLine1');
        if (instantLine1 && instantLine1 !== 'DataGate.DatagateSidebarProjectLine1') {
          this.projectLine1 = instantLine1;
        }
      } catch (e) {
        // Fall through to get()
      }
      
      this.translateService.get('DataGate.DatagateSidebarProjectLine1').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (x && x !== 'DataGate.DatagateSidebarProjectLine1') {
          this.projectLine1 = x;
        }
      });

      try {
        const instantLine2 = this.translateService.instant('DataGate.DatagateSidebarProjectLine2');
        if (instantLine2 && instantLine2 !== 'DataGate.DatagateSidebarProjectLine2') {
          this.projectLine2 = instantLine2;
          this.loading = false;
        }
      } catch (e) {
        // Fall through to get()
      }
      
      this.translateService.get('DataGate.DatagateSidebarProjectLine2').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (x && x !== 'DataGate.DatagateSidebarProjectLine2') {
          this.projectLine2 = x;
          this.loading = false;
        }
      });
    } else {
      // Use regular Sidebar i18n keys for other routes
      this.translateService.get('Sidebar.Title').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        // Nur setzen, wenn noch kein Wert vorhanden ist (API darf Vorrang haben)
        if (!this.title) {
          this.title = x;
        }
      });
      this.translateService.get('Sidebar.Claim').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (!this.projectClaim) {
          this.projectClaim = x;
        }
      });
      this.translateService.get('Sidebar.ProjectLine1').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (!this.projectLine1) {
          this.projectLine1 = x;
        }
      });
      this.translateService.get('Sidebar.ProjectLine2').pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe((x) => {
        if (!this.projectLine2) {
          this.projectLine2 = x;
        }
        this.loading = false;
      });
    }
  }

  private loadProjectShortnameFromRoute(): void {
    // Try to get from current route params
    let projectShortname = this.activatedRoute.snapshot.paramMap.get('projectShortname');
    
    // Try from route params (alternative syntax)
    if (!projectShortname) {
      projectShortname = this.activatedRoute.snapshot.params['projectShortname'];
    }
    
    // Try from parent route params
    if (!projectShortname && this.activatedRoute.parent) {
      projectShortname = this.activatedRoute.parent.snapshot.paramMap.get('projectShortname');
      if (!projectShortname) {
        projectShortname = this.activatedRoute.parent.snapshot.params['projectShortname'];
      }
    }

    // If we found a project shortname and it's different, update and load details
    if (projectShortname && projectShortname !== this.projectShortname) {
      this.projectShortname = projectShortname;
      // Update themeService so other components can use it
      this.themeService.setProjectShortName(this.projectShortname);
      // Reload project details from API only if not on data-gate route
      if (!this.isDataGateRoute) {
        this.themeService.getProjectDetails();
      }
    }
  }

  themeOptionalProjectsettings() {
    //get optional settings for a theme especially for sidebar
    // This method is kept for backward compatibility
    // Project details are now loaded automatically via setupProjectDetailsSubscription()
    if (this.projectShortname) {
      this.themeService.getProjectDetails();
    }
  }

  scrollToMainContent() {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(false);
    this.ngUnsubscribe.complete();
  }
}
